export type AthleteSport =
  | 'football'
  | 'basketball'
  | 'baseball'
  | 'soccer'
  | 'track_and_field'
  | 'volleyball'
  | 'softball'
  | 'lacrosse'
  | 'field_hockey';
export type ContactStatus = 'New' | 'Contacted' | 'Interested' | 'Offer Received';
export type UserRole = 'Athlete' | 'Coach' | 'AD' | 'Staff';
export type SubscriptionPlan = 'monthly' | 'lifetime' | 'school_site_license' | 'none';
export type SubscriptionStatus = 'active' | 'trial' | 'expired' | 'none';
export type OpportunityType = 'camp' | 'combine' | 'scouting' | 'other' | 'film';
export type VerificationStatus = 'pending' | 'verified' | 'rejected';
export type Division = 'D1' | 'D2' | 'D3' | 'NAIA' | 'JUCO' | 'None';

// Type-safe Navigation Constants
export const TABS = {
  HUB: 'Hub',
  TEAM: 'Team',
  DEPT: 'Dept',
  LOCKER: 'Locker',
  CRM: 'CRM',
  INBOX: 'Inbox',
  SHOP: 'Shop',
  SETTINGS: 'Settings',
  LAB: 'Lab',
} as const;

export type AppTab = (typeof TABS)[keyof typeof TABS];

/**
 * A parent / guardian / emergency contact on an athlete profile.
 * Stored as a jsonb array on profiles.guardians. PRIVATE PII — only ever
 * surfaced to connected coaches, never in public exports or ScoutPass.
 */
export interface Guardian {
  name: string;
  relationship?: string; // "Mother", "Father", "Guardian", "Emergency", etc.
  email?: string;
  phone?: string;
}

export interface Profile {
  id: string;
  full_name: string;
  avatar_url?: string | null;
  role: UserRole;
  is_admin?: boolean;
  school_id?: string;
  sport: AthleteSport;
  positions: string[];
  height?: string;
  weight?: string;
  grad_year: number;
  hudl_link?: string;
  school?: string;
  gpa?: number;
  sat_score?: number;
  act_score?: number;
  social_handles: {
    twitter?: string;
    instagram?: string;
  };
  featured_stats?: string[];
  subscription_status: SubscriptionStatus;
  subscription_plan: SubscriptionPlan;
  trial_started_at?: string;
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
  birth_year?: number | null;
  // --- Recruiting Info (added April 2026) ---
  jersey_number?: string;
  ncaa_id?: string;
  // Private PII — surfaced to connected coaches only, never in public exports.
  street_address?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  guardians?: Guardian[];
  // Handles (no leading @) auto-appended to SocialStudio caption exports.
  default_mentions?: string[];
  // Set to 'founding' when the athlete signed up via a founding-spot promo (e.g. /independence).
  promo_source?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface UpdateProfileDTO extends Partial<
  Omit<Profile, 'id' | 'created_at' | 'updated_at'>
> {}

export interface Organization {
  id: string;
  name: string;
  colors: { primary: string; secondary: string };
  logo_url?: string;
  subscription_tier: 'Basic' | 'Pro' | 'Elite';
  athlete_count: number;
  coach_count: number;
}

export interface Stat {
  id: string;
  user_id: string;
  category: string;
  name: string;
  value: string;
  date_recorded: string;
  video_proof_url?: string;
  verification_status: VerificationStatus;
  verified_by?: string;
  created_at?: string;
}

export interface CreateStatDTO extends Omit<Stat, 'id' | 'created_at'> {}

export interface Contact {
  id: string;
  user_id: string;
  name: string;
  nickname: string;
  role: string;
  school_name: string;
  twitter_handle?: string;
  twitter_recipient_id?: string;
  instagram_handle?: string;
  phone_number?: string;
  status: ContactStatus;
  last_contacted_at?: string;
  created_at?: string;
  is_direct_connect?: boolean;
  division?: Division;
  is_priority?: boolean;
  // Set when the contact was added from the licensed college_coaches directory.
  // Used to prevent duplicate "Add to Contacts" from Find Coaches search.
  source_coach_id?: string | null;
}

export interface UpsertContactDTO extends Partial<Omit<Contact, 'created_at'>> {
  user_id: string;
  name: string;
}

export interface OutreachTemplate {
  id: string;
  user_id: string;
  title: string;
  body: string;
  created_at?: string;
}

export interface UpsertTemplateDTO extends Partial<Omit<OutreachTemplate, 'created_at'>> {
  user_id: string;
}

export interface Message {
  id: string;
  sender_id: string;
  receiver_id: string;
  content: string;
  timestamp?: string;
  created_at?: string;
  is_read?: boolean;
  type?: string;
  metadata?: Record<string, unknown>;
}

export interface Conversation {
  id: string;
  participant: {
    full_name: string;
    avatar_url?: string | null;
  };
  unreadCount: number;
  lastMessage: Message;
}

export interface Opportunity {
  id: string;
  title: string;
  provider: string;
  location: string;
  date: string;
  price: string;
  type: OpportunityType;
  url: string;
  trending?: boolean;
}

export interface FeedItem {
  id: string;
  user: Profile;
  type: 'stat_update' | 'offer_received' | 'status' | 'video_shared';
  content: string;
  data?: Record<string, string>;
  timestamp: string;
  likes: number;
  comments: number;
}

export interface School {
  id: string;
  name: string;
  division: string;
  conference: string;
}

// --- Coach Roster & Categories (added April 2026) ---

export type TeamConnectionStatus = 'pending' | 'active' | 'declined';
export type TeamConnectionSource = 'qr_scan' | 'invite' | 'direct_add';

export interface TeamConnection {
  id: string;
  coach_id: string;
  athlete_id: string;
  status: TeamConnectionStatus;
  added_via: TeamConnectionSource;
  created_at?: string;
  responded_at?: string | null;
}

export interface AthleteInvite {
  id: string;
  coach_id: string;
  invitee_email: string;
  created_at?: string;
  expires_at: string;
  accepted_at?: string | null;
}

export interface AthleteCategory {
  id: string;
  coach_id: string;
  name: string;
  color: string; // 'blue' | 'emerald' | 'amber' | 'rose' | 'slate'
  description?: string;
  created_at?: string;
  member_count?: number; // hydrated client-side
}

export interface UpsertCategoryDTO {
  id?: string;
  coach_id: string;
  name: string;
  color?: string;
  description?: string;
}

export interface CategoryMember {
  id: string;
  category_id: string;
  athlete_id: string;
  added_at?: string;
  notes?: string;
}

// --- Category Shares (added April 2026) ---

export interface CategoryShare {
  id: string;
  category_id: string;
  coach_id: string;
  share_token: string;
  recipient_email?: string | null;
  optional_message?: string | null;
  require_email_to_view: boolean;
  expires_at?: string | null;
  revoked_at?: string | null;
  view_count: number;
  last_viewed_at?: string | null;
  created_at: string;
}

export type CategoryShareStatus = 'active' | 'expired' | 'revoked';

/** Shape returned by the public get-shared-category edge function. */
export interface SharedCategoryView {
  category: {
    id: string;
    name: string;
    description?: string;
  };
  coach: {
    full_name: string;
    school?: string;
  };
  optional_message?: string;
  athletes: SharedCategoryAthlete[];
  require_email_to_view: boolean;
  email_captured: boolean;
}

/**
 * PII-safe athlete subset for shared category views. Mirrors the public
 * ScoutPass shape. Never includes street_address, city, state, postal_code,
 * guardians, ncaa_id, birth_year, subscription_*, stripe_*, or is_admin.
 * Email IS included so scouts can follow up directly.
 */
export interface SharedCategoryAthlete {
  id: string;
  full_name: string;
  avatar_url?: string | null;
  sport: AthleteSport;
  positions: string[];
  grad_year: number;
  school?: string;
  height?: string;
  weight?: string;
  gpa?: number;
  sat_score?: number;
  act_score?: number;
  hudl_link?: string;
  social_handles: { twitter?: string; instagram?: string };
  featured_stats?: string[];
  jersey_number?: string;
  email?: string;
  top_stats: { metric: string; value: number }[];
}

/**
 * PII-safe athlete profile for the public ScoutPass page (/scout/:athleteId).
 * Same whitelist as SharedCategoryAthlete. Populated by get-athlete-public-profile.
 */
export interface PublicAthleteProfile {
  id: string;
  full_name: string;
  avatar_url?: string | null;
  sport: AthleteSport;
  positions: string[];
  grad_year: number;
  school?: string;
  height?: string;
  weight?: string;
  gpa?: number;
  sat_score?: number;
  act_score?: number;
  hudl_link?: string;
  social_handles: { twitter?: string; instagram?: string };
  featured_stats?: string[];
  jersey_number?: string;
  top_stats: { metric: string; value: number }[];
}

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
}

// --- Coach Hub Announcements (added April 2026) ---

export interface CoachAnnouncement {
  id: string;
  coach_id: string;
  title: string;
  body?: string | null;
  url?: string | null;
  url_label?: string | null;
  starts_at: string;
  ends_at: string;
  priority: number;
  created_at: string;
}

export type CreateAnnouncementDTO = Omit<CoachAnnouncement, 'id' | 'created_at'>;

// --- College Coach Directory (Find Coaches search, added Sept 2026) ---
//
// Licensed data from contactcollegecoaches.com. May only ever be shown behind
// the authenticated app shell — never a public/unauthenticated route. The
// `college_coaches` table's RLS is authenticated-only for that reason.

/** One flattened row from the `college_coaches` directory search. */
export interface CollegeCoachResult {
  id: string;
  first_name: string | null;
  last_name: string | null;
  position_title: string | null;
  // Raw vendor granular value: FBS / FCS / NCAA DII / NCAA DIII / NAIA / JC /
  // JC-CCCAA. Not this app's coarser Division type — map with mapCoachDivision().
  division: string | null;
  conference: string | null;
  state: string | null;
  email: string | null;
  phone: string | null;
  twitter_handle: string | null;
  instagram_handle: string | null;
  school_name: string | null;
}

export interface CoachSearchFilters {
  /** Raw vendor division values (see CollegeCoachResult.division). */
  division: string[];
  conference: string;
  state: string;
}
